'use client';

import { useMemo, useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { cn } from '../../utils/cn';
import type { TopCategoriesProps, CategoryItem } from './types';

/**
 * Компонент для отображения топ категорий с поддержкой многоязычности и настраиваемой grid-сетки
 */
export function TopCategories({
  categories,
  translations = {},
  limit,
  gridConfig,
  langCode,
  categoryPath = '/shop/category',
  viewAllButtonText,
  className = '',
  fallbackImageUrl = '/placeholder.png',
}: TopCategoriesProps) {
  // Ограничиваем количество категорий, если указан limit
  const limitedCategories = useMemo(() => {
    if (limit && limit > 0) {
      return categories.slice(0, limit);
    }
    return categories;
  }, [categories, limit]);

  // Извлекаем данные категории (поддержка разных форматов)
  const getCategoryData = useCallback((category: CategoryItem) => {
    const code = category.code || category.CODE || '';
    
    // Используем перевод, если доступен langCode и перевод существует
    let name = '';
    if (langCode && category.translation?.[langCode]?.name) {
      name = category.translation[langCode].name;
    } else {
      // Fallback на базовое поле name
      name = category.name || category.NAME || '';
    }
    
    const imageUrl = category.picture || category.IMAGE_URL || fallbackImageUrl;
    
    return { code, name, imageUrl };
  }, [fallbackImageUrl, langCode]);

  // Формируем классы для grid-сетки
  const gridClasses = useMemo(() => {
    if (!gridConfig) {
      // Дефолтная конфигурация: 2 колонки на мобильных, 4 на больших экранах
      return 'flex overflow-x-auto scrollbar-hide pb-4 gap-3 md:grid md:gap-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4';
    }

    const classes: string[] = [];
    
    // Маппинг для grid-cols классов Tailwind
    const gridColsMap: Record<number, string> = {
      1: 'grid-cols-1',
      2: 'grid-cols-2',
      3: 'grid-cols-3',
      4: 'grid-cols-4',
      5: 'grid-cols-5',
      6: 'grid-cols-6',
      7: 'grid-cols-7',
      8: 'grid-cols-8',
      9: 'grid-cols-9',
      10: 'grid-cols-10',
      11: 'grid-cols-11',
      12: 'grid-cols-12',
    };
    
    // Для мобильных устройств используем flex с прокруткой
    if (gridConfig.mobile) {
      classes.push('flex overflow-x-auto scrollbar-hide pb-4 gap-3');
      classes.push('md:grid md:gap-3');
    } else {
      // Если mobile не указан, используем grid везде
      classes.push('grid gap-3');
    }

    // Добавляем классы для разных breakpoints
    if (gridConfig.sm && gridColsMap[gridConfig.sm]) {
      classes.push(`sm:${gridColsMap[gridConfig.sm]}`);
    }
    if (gridConfig.md && gridColsMap[gridConfig.md]) {
      classes.push(`md:${gridColsMap[gridConfig.md]}`);
    }
    if (gridConfig.lg && gridColsMap[gridConfig.lg]) {
      classes.push(`lg:${gridColsMap[gridConfig.lg]}`);
    }
    if (gridConfig.xl && gridColsMap[gridConfig.xl]) {
      classes.push(`xl:${gridColsMap[gridConfig.xl]}`);
    }

    return classes.join(' ');
  }, [gridConfig]);

  // Заголовок из переводов или дефолтный
  const title = translations.title || 'Топ категорий';
  // Текст кнопки из переводов, пропсов или дефолтный
  const buttonText = translations.viewAllButtonText || viewAllButtonText || 'Все категории';
  
  // Формируем путь для кнопки "Все категории" - убираем /category из categoryPath
  const viewAllPath = useMemo(() => {
    if (!categoryPath) return '/shop';
    // Убираем /category в конце пути
    return categoryPath.replace(/\/category\/?$/, '') || '/shop';
  }, [categoryPath]);

  if (!limitedCategories || limitedCategories.length === 0) {
    return null;
  }

  return (
    <div className={cn('bg-white', className)}>
      {title && (
        <div className="flex items-center justify-between mt-8">
          <h2 className="text-large font-medium md:text-2xl">
            {title}
          </h2>
          {viewAllPath && (
            <Link
              href={viewAllPath}
              className={cn(
                'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors',
                'h-8 px-3 text-xs',
                'ml-4',
                'bg-[#0a1a29] text-white',
                'hover:bg-[#0d2135]',
                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#0a1a29] focus-visible:ring-offset-1',
                'disabled:pointer-events-none disabled:opacity-50',
                'shadow hover:opacity-90'
              )}
            >
              {buttonText}
            </Link>
          )}
        </div>
      )}
      
      <div className="card_list">
        <div className={gridClasses}>
          {limitedCategories.map((category, index) => {
            const { code, name, imageUrl } = getCategoryData(category);
            
            if (!code) {
              return null;
            }

            return (
              <div
                key={code || index}
                className="flex-none w-[40%] md:w-full md:col my-1"
              >
                <Link
                  href={`${categoryPath}/${code}`}
                  className="flex flex-col card_link h-full py-1 md:py-2 lg:py-2"
                >
                  <Image
                    alt={name}
                    src={imageUrl}
                    width={300}
                    height={300}
                    className="w-full rounded-lg border object-cover group-hover:opacity-75 aspect-[16/11]"
                  />
                  <div className="news_body mt-0 md:mt-2 flex justify-center md:justify-start flex-col">
                    <div className="mb-1 text-left font-medium text-small">
                      {name}
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
